interface SignupTrackingData {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  ref: string | null;
  signup_source: string;
  captured_at: string;
}

interface SignupTrackingParams {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  ref?: string;
  signupSource?: string;
}

const STORAGE_KEY = "coachli_utm_data";

function mapReferrerToSource(referrer: string): string {
  if (!referrer) return "direct";

  try {
    const referrerUrl = new URL(referrer);
    const referrerHostname = referrerUrl.hostname.toLowerCase();
    const currentHostname = window.location.hostname.toLowerCase();

    // Same origin = direct
    if (referrerHostname === currentHostname) return "direct";

    // Known sources
    if (
      referrerHostname.includes("google.com") ||
      referrerHostname.includes("google.co")
    ) {
      return "google_organic";
    }
    if (referrerHostname.includes("bing.com")) {
      return "bing_organic";
    }
    if (referrerHostname.includes("twitter.com") || referrerHostname.includes("t.co")) {
      return "twitter_organic";
    }
    if (
      referrerHostname.includes("linkedin.com") ||
      referrerHostname.includes("lnkd.in")
    ) {
      return "linkedin_organic";
    }
    if (
      referrerHostname.includes("instagram.com") ||
      referrerHostname.includes("l.instagram.com")
    ) {
      return "instagram_organic";
    }
    if (referrerHostname.includes("tiktok.com")) {
      return "tiktok_organic";
    }
    if (
      referrerHostname.includes("facebook.com") ||
      referrerHostname.includes("fb.com")
    ) {
      return "facebook_organic";
    }
    if (referrerHostname.includes("youtube.com")) {
      return "youtube_organic";
    }

    // this will return raw domain for unknown sources
    return referrerHostname;
  } catch (e) {
    return "direct";
  }
}

export function captureSignupSource(): void {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return;
  }

  try {
    const params = new URLSearchParams(window.location.search);

    // Checking if we have any UTM params or ref
    const utmSource = params.get("utm_source");
    const utmMedium = params.get("utm_medium");
    const utmCampaign = params.get("utm_campaign");
    const utmContent = params.get("utm_content");
    const utmTerm = params.get("utm_term");
    const ref = params.get("ref");

    const hasTrackingParams = !!(
      utmSource ||
      utmMedium ||
      utmCampaign ||
      utmContent ||
      utmTerm ||
      ref
    );

    if (hasTrackingParams) {
      // UTM params present - overwrite storage
      const trackingData: SignupTrackingData = {
        utm_source: utmSource || null,
        utm_medium: utmMedium || null,
        utm_campaign: utmCampaign || null,
        utm_content: utmContent || null,
        utm_term: utmTerm || null,
        ref: ref || null,
        signup_source: utmSource || "direct",
        captured_at: new Date().toISOString(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(trackingData));

      // Clean the URL
      if (
        window.location.search.includes("utm_") ||
        window.location.search.includes("ref=")
      ) {
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } else {
      // No UTM params - check if we already have stored data
      const existingData = localStorage.getItem(STORAGE_KEY);

      if (!existingData) {
        // No existing data - apply referrer fallback
        const signupSource = mapReferrerToSource(document.referrer);

        const trackingData: SignupTrackingData = {
          utm_source: null,
          utm_medium: null,
          utm_campaign: null,
          utm_content: null,
          utm_term: null,
          ref: null,
          signup_source: signupSource,
          captured_at: new Date().toISOString(),
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(trackingData));
      }
      // If existing data present, do nothing (preserve it)
    }
  } catch (e) {
    console.error("Error capturing signup source:", e);
  }
}

export function getSignupTrackingParams(): SignupTrackingParams {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return {};
  }

  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return {};

    const trackingData: SignupTrackingData = JSON.parse(data);
    const params: SignupTrackingParams = {};

    if (trackingData.utm_source) params.utmSource = trackingData.utm_source;
    if (trackingData.utm_medium) params.utmMedium = trackingData.utm_medium;
    if (trackingData.utm_campaign) params.utmCampaign = trackingData.utm_campaign;
    if (trackingData.utm_content) params.utmContent = trackingData.utm_content;
    if (trackingData.utm_term) params.utmTerm = trackingData.utm_term;
    if (trackingData.ref) params.ref = trackingData.ref;
    if (trackingData.signup_source) params.signupSource = trackingData.signup_source;

    return params;
  } catch (e) {
    console.error("Error reading signup tracking params:", e);
    return {};
  }
}

export function clearSignupTrackingData(): void {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error("Error clearing signup tracking data:", e);
  }
}
